import Phaser from 'phaser'
import { gameBus } from '../../bus/GameEventBus'
import { itemName, partLabel, t, weaponName, weatherName } from '../../i18n'
import { matchController, type OccupantView, type PlayerView } from '../../services/JKGame_MatchController'
import { LOOT_ITEMS, LOOT_WEAPONS, WEATHER_FOG_INDEXES, ZONES } from '../../sim/JKGame_content'
import { MOVE_COST, MOVE_COST_HURT } from '../../sim/JKGame_match'
import type { Weapon } from '../../sim/JKGame_types'
import { ARCH_TINT, borderObj, objKey, zoneDesc, zoneFloorTile, zoneLore, zoneName, zoneTheme, zoneUrbanProps } from '../JKGame_assets'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { DIRS, type Dir, neighborOf, opposite } from '../JKGame_island'
import { COLORS, FONT, hasFinePointer, hpColor, hpColorHex, makeButton, makeConfirmPanel, prefersReducedMotion } from '../JKGame_ui'
import type { JKGame_ResultData } from './JKGame_ResultScene'

const TILE = 58
const GW = 7
const GH = 10
const ROOM_X = Math.round((DESIGN_WIDTH - GW * TILE) / 2)
const ROOM_Y = 132
const FOG_VISION = 2 // tiles visible around the player in foggy weather
const FOG_WEATHER = new Set<number>(WEATHER_FOG_INDEXES) // 起霧 / 濃霧（來自天候 pack）
// Scannable icon per weather index (aligned to i18n weather.w0..w9: 晴/大晴/多雲/小雨/
// 暴雨/颱風/雷雨/雪/霧/濃霧). Display only; out-of-range falls back to no icon.
const WEATHER_ICON = ['☀️', '🌞', '⛅', '🌦️', '🌧️', '🌀', '⛈️', '🌨️', '🌫️', '🌫️']

interface Loot {
  kind: 'weapon' | 'item'
  weapon?: Weapon
  item?: string
}
interface BotSpr {
  spr: Phaser.GameObjects.Sprite
  tag: Phaser.GameObjects.Text
  gx: number
  gy: number
}

function key(gx: number, gy: number): string {
  return `${gx},${gy}`
}

export class JKGame_ExploreScene extends Phaser.Scene {
  private zone = 0
  private px = 3
  private py = 5
  private exitTiles = new Map<string, Dir>()
  private lootAt = new Map<string, { loot: Loot; spr: Phaser.GameObjects.Image }>()
  private botSprites = new Map<string, BotSpr>()
  private blocked = new Set<string>()
  private layer!: Phaser.GameObjects.Container
  private playerSpr!: Phaser.GameObjects.Sprite
  private bumpTween?: Phaser.Tweens.Tween
  private inCombat = false
  private transitioning = false
  private over = false // true after the first match:over → onOver fires its Result transition exactly once
  private overTimer?: number // window timer id for the delayed Result start (cleared on shutdown)
  private disposers: Array<() => void> = []
  private idleTimer?: Phaser.Time.TimerEvent
  private wanderTimer?: Phaser.Time.TimerEvent
  private areaCard?: Phaser.GameObjects.Container
  private objectiveCard?: Phaser.GameObjects.Container
  private activeToast?: Phaser.GameObjects.Text // only one toast at a time so reasons stay legible
  private logDrawer?: Phaser.GameObjects.Container
  private areaDrawer?: Phaser.GameObjects.Container
  private visitedZones = new Set<number>() // zones whose lore has been shown (first-visit only)

  // HUD refs
  private zoneTxt!: Phaser.GameObjects.Text
  private subTxt!: Phaser.GameObjects.Text
  private aliveTxt!: Phaser.GameObjects.Text
  private shrinkTxt!: Phaser.GameObjects.Text
  private killsTxt!: Phaser.GameObjects.Text
  private injuryTxt!: Phaser.GameObjects.Text
  private keyTxt!: Phaser.GameObjects.Text
  private escBtn?: Phaser.GameObjects.Container // dimmed until the player holds the escape key
  private quitBox?: Phaser.GameObjects.Container // quit-to-menu confirmation panel (no dead end)
  private escapeTween?: Phaser.Tweens.Tween // "you can escape now" pulse once the key is held
  private hpFill!: Phaser.GameObjects.Rectangle
  private spFill!: Phaser.GameObjects.Rectangle
  private breatheTween?: Phaser.Tweens.Tween // subtle player idle breathing (scale)
  private hpBarTween?: Phaser.Tweens.Tween // smooth HP-bar change (matches Combat)
  private spBarTween?: Phaser.Tweens.Tween // smooth SP-bar change
  private hpValTxt!: Phaser.GameObjects.Text // numeric HP on the bar (parity with Combat)
  private spValTxt!: Phaser.GameObjects.Text
  private wepTxt!: Phaser.GameObjects.Text
  private prevWeaponName?: string // last-rendered weapon; a change briefly flashes the line
  private sleepBtn?: Phaser.GameObjects.Container // dimmed when SP is already full (rest wastes a turn)
  private healBtn?: Phaser.GameObjects.Container  // dimmed when HP is already full
  private feedTxt!: Phaser.GameObjects.Text
  private dangerTxt!: Phaser.GameObjects.Text
  private dangerTween?: Phaser.Tweens.Tween

  constructor() {
    super('Explore')
  }

  create(): void {
    this.inCombat = false
    this.transitioning = false
    this.over = false
    this.overTimer = undefined
    this.lootAt = new Map()
    this.botSprites = new Map()
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(280, 0, 0, 0) // entry reveal (once; room swaps use their own slide)

    this.zone = this.player().zone
    this.buildHud()
    this.buildControls()
    this.buildRoom(this.zone, undefined)
    this.showObjectiveCard()
    this.renderHud()

    this.disposers.push(gameBus.on('match:updated', () => this.onUpdated()))
    this.disposers.push(gameBus.on('match:over', () => this.onOver()))
    this.idleTimer = this.time.addEvent({ delay: 2600, loop: true, callback: () => matchController.idleTick() })
    this.wanderTimer = this.time.addEvent({ delay: 1200, loop: true, callback: () => this.wanderBots() })

    // Desktop movement: arrow keys / WASD mirror the on-screen D-pad. tryStep
    // already guards combat/transition; scene-scoped listeners die on shutdown.
    const kb = this.input.keyboard
    kb?.on('keydown-UP', () => this.tryStep('N'))
    kb?.on('keydown-DOWN', () => this.tryStep('S'))
    kb?.on('keydown-LEFT', () => this.tryStep('W'))
    kb?.on('keydown-RIGHT', () => this.tryStep('E'))
    kb?.on('keydown-W', () => this.tryStep('N'))
    kb?.on('keydown-S', () => this.tryStep('S'))
    kb?.on('keydown-A', () => this.tryStep('W'))
    kb?.on('keydown-D', () => this.tryStep('E'))
    // Non-movement actions mirror the on-screen buttons (same intents): R/H rest, E escape.
    // Rest/escape keys, guarded against firing behind the open quit-confirm modal (it blocks
    // pointer input but Phaser keyboard events still reach the scene). Movement is guarded in tryStep.
    kb?.on('keydown-R', () => { if (!this.hasModalOpen()) gameBus.emit('intent:rest', { kind: 'sleep' }) })
    kb?.on('keydown-H', () => { if (!this.hasModalOpen()) gameBus.emit('intent:rest', { kind: 'heal' }) })
    kb?.on('keydown-E', () => this.tryEscape())
    kb?.on('keydown-L', () => this.showLogDrawer())
    kb?.on('keydown-I', () => this.showAreaDrawer())
    kb?.on('keydown-ESC', () => {
      if (this.areaDrawer) this.dismissAreaDrawer()
      else if (this.logDrawer) this.dismissLogDrawer()
      else if (this.quitBox) this.dismissQuitConfirm()
      else this.showQuitConfirm()
    }) // quit-to-menu (with confirm)

    // Pass the method + context (not an arrow) so cleanup() can remove THIS exact listener.
    // Scene `events` listeners survive a scene restart (the instance is reused), so an
    // un-removed `.on(RESUME)` would accumulate across Explore restarts (Menu/Result/Story all
    // scene.start('Explore')) → afterCombat firing N times per combat-resume. (#160 footgun.)
    this.events.on(Phaser.Scenes.Events.RESUME, this.afterCombat, this)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup())
  }

  private player(): PlayerView {
    return matchController.getPlayerView()
  }

  private hasModalOpen(): boolean {
    return !!(this.quitBox || this.logDrawer || this.areaDrawer)
  }

  // ─── HUD ───────────────────────────────────────────────────────────────────

  private buildHud(): void {
    this.add.rectangle(0, 0, DESIGN_WIDTH, 116, COLORS.panel).setOrigin(0, 0).setStrokeStyle(1, COLORS.panelEdge)
    this.zoneTxt = this.add.text(16, 12, '', { fontFamily: FONT, fontSize: '24px', color: COLORS.text, fontStyle: 'bold' })
    this.subTxt = this.add.text(16, 42, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.sub })
    this.aliveTxt = this.add.text(DESIGN_WIDTH - 16, 12, '', { fontFamily: FONT, fontSize: '20px', color: COLORS.good }).setOrigin(1, 0)
    this.shrinkTxt = this.add.text(DESIGN_WIDTH - 16, 40, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.sub }).setOrigin(1, 0)
    this.killsTxt = this.add.text(DESIGN_WIDTH - 16, 60, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.sub }).setOrigin(1, 0)
    this.keyTxt = this.add.text(DESIGN_WIDTH - 16, 78, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.good }).setOrigin(1, 0)
    this.injuryTxt = this.add.text(DESIGN_WIDTH - 16, 96, '', { fontFamily: FONT, fontSize: '12px', color: COLORS.bad }).setOrigin(1, 0)

    this.add.rectangle(16, 78, 150, 12, COLORS.barBg).setOrigin(0, 0)
    this.hpFill = this.add.rectangle(16, 78, 150, 12, COLORS.hp).setOrigin(0, 0)
    this.add.rectangle(16, 96, 150, 12, COLORS.barBg).setOrigin(0, 0)
    this.spFill = this.add.rectangle(16, 96, 150, 12, COLORS.sp).setOrigin(0, 0)
    // Numeric HP/SP centered on the bars (parity with the Combat HUD #71/#80); a thin
    // dark stroke keeps them readable over the variable bar colour.
    this.hpValTxt = this.add.text(91, 84, '', { fontFamily: FONT, fontSize: '10px', color: '#ffffff' }).setOrigin(0.5).setStroke('#0b0e14', 2)
    this.spValTxt = this.add.text(91, 102, '', { fontFamily: FONT, fontSize: '10px', color: '#ffffff' }).setOrigin(0.5).setStroke('#0b0e14', 2)
    this.wepTxt = this.add.text(176, 86, '', { fontFamily: FONT, fontSize: '14px', color: COLORS.text })
    this.feedTxt = this.add.text(DESIGN_WIDTH / 2, ROOM_Y + GH * TILE + 10, '', {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.sub,
      align: 'center',
      backgroundColor: '#0b1018cc',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5, 0).setDepth(35)
    this.feedTxt.setInteractive({ useHandCursor: true }).on('pointerup', () => this.showLogDrawer())

    this.dangerTxt = this.add.text(DESIGN_WIDTH / 2, ROOM_Y - 4, '', { fontFamily: FONT, fontSize: '14px', color: COLORS.bad }).setOrigin(0.5, 1)
  }

  private renderHud(): void {
    const p = this.player()
    this.zoneTxt.setText(`${zoneName(this.zone)}　${ZONES[this.zone].coord}`)
    // Weather line; when fog is limiting vision (see applyFog), say so in plain copy.
    const wi = matchController.getWeather()
    const icon = WEATHER_ICON[wi] ?? ''
    const weatherLine = t('hud.weather', { weather: `${icon}${icon ? ' ' : ''}${weatherName(wi)}` })
    this.subTxt.setScale(1) // reset before measuring (a prior longer line may have shrunk it)
    this.subTxt.setText(this.isFoggy() ? `${weatherLine}　${t('hud.fogHint')}` : weatherLine)
    // Left-anchored at x=16; shrink a long localized weather+fog line so it never runs off-screen.
    const maxSubW = DESIGN_WIDTH - 24
    if (this.subTxt.width > maxSubW) this.subTxt.setScale(maxSubW / this.subTxt.width)
    const aliveCount = matchController.getAliveCount()
    this.aliveTxt.setText(t('hud.alive', { alive: aliveCount, total: matchController.getTotal() }))
    // Amber the survivor count in the tense endgame stretch (≤4 left), echoing the
    // other heightened-alert cues; back to green earlier in the match.
    this.aliveTxt.setColor(aliveCount <= 4 ? '#ffb24d' : COLORS.good)
    // Amber the shrink countdown when a seal is imminent (≤2 steps) so the urgency
    // reads at a glance, echoing the zone-danger pulse. Cosmetic; same in both modes.
    const stepsToShrink = matchController.getStepsToShrink()
    this.shrinkTxt.setText(t('hud.shrink', { steps: stepsToShrink }))
    this.shrinkTxt.setColor(stepsToShrink <= 2 ? '#ffb24d' : COLORS.sub)
    this.killsTxt.setText(t('hud.kills', { n: p.kills }))
    this.keyTxt.setText(p.hasKey ? t('hud.key') : '')
    // Dim the escape button until the player actually holds the key — escape is only
    // possible with it (still tappable; a keyless tap explains why via toast). Once the
    // key is held, a gentle pulse announces "you can escape now → win"; the tween owns
    // the alpha while keyed, so we don't fight it by re-setting alpha every frame.
    if (p.hasKey) {
      if (prefersReducedMotion()) {
        this.escBtn?.setAlpha(1) // keyed → full alpha (un-dimmed signals "available"); no pulse
      } else if (!this.escapeTween && this.escBtn) {
        this.escBtn.setAlpha(1)
        this.escapeTween = this.tweens.add({ targets: this.escBtn, alpha: { from: 1, to: 0.55 }, duration: 620, yoyo: true, repeat: -1 })
      }
    } else {
      this.escapeTween?.stop()
      this.escapeTween = undefined
      this.escBtn?.setAlpha(0.5)
    }
    // Persistent injury indicator (matches the Combat HUD): localized body parts.
    const inj = [...p.injuries].sort().map((c) => partLabel(c)).join('・')
    this.injuryTxt.setScale(1) // reset before measuring (a prior longer set may have shrunk it)
    this.injuryTxt.setText(inj ? `🩹 ${inj}` : '')
    // Right-anchored; cap the width so a long all-parts list (e.g. en-US 4 injuries) never
    // reaches across into the left-column HUD. Shrinks toward the right edge.
    const maxInjW = 220
    if (this.injuryTxt.width > maxInjW) this.injuryTxt.setScale(maxInjW / this.injuryTxt.width)
    const hpRatio = Math.max(0, Math.min(1, p.hp / p.mhp))
    // Smooth HP/SP bar changes (consistency with the Combat HUD #93–95); restart the
    // tween each render so rapid updates never stack.
    this.hpBarTween?.stop()
    this.hpBarTween = this.tweens.add({ targets: this.hpFill, scaleX: hpRatio, duration: 220, ease: 'Cubic.Out' })
    this.hpFill.setFillStyle(hpColor(hpRatio)) // amber warning when low (matches Combat HUD)
    this.hpValTxt.setText(`${Math.max(0, p.hp)} / ${p.mhp}`).setColor(hpColorHex(hpRatio)) // value matches bar (#99); "X / Y" format parity with the Combat HUD
    this.spBarTween?.stop()
    this.spBarTween = this.tweens.add({ targets: this.spFill, scaleX: Math.max(0, Math.min(1, p.sp / p.msp)), duration: 220, ease: 'Cubic.Out' })
    // Amber (bar + value) when stamina can't afford the next step (mirrors the noSp
    // move gate), so the player sees "rest now" before a move is rejected.
    const moveCost = p.injuries.has('f') ? MOVE_COST_HURT : MOVE_COST
    const lowSp = p.sp <= moveCost
    this.spFill.setFillStyle(lowSp ? 0xffb24d : COLORS.sp)
    this.spValTxt.setText(`${Math.max(0, p.sp)} / ${p.msp}`).setColor(lowSp ? '#ffb24d' : '#ffffff') // "X / Y" parity with the Combat HUD
    // Briefly gild the weapon line when the loadout changes (e.g. picking up a new
    // weapon) — a HUD confirmation beyond the at-tile pickup float. Colour-only (no
    // motion), so it needs no reduced-motion gate; restores after a beat. Skip the first
    // render (prevWeaponName undefined) so it never flashes on entry.
    const wepChanged = this.prevWeaponName !== undefined && this.prevWeaponName !== p.weaponName
    const ammoTag = p.weaponAmmo >= 0 ? ` ×${p.weaponAmmo}${p.weaponAmmo <= 3 ? ' ⚠' : ''}` : '' // low-ammo cue at ≤3
    this.wepTxt.setText(t('status.weapon', { w: weaponName(p.weaponName) + ammoTag }))
    this.prevWeaponName = p.weaponName
    if (wepChanged) {
      this.wepTxt.setColor('#ffd84d')
      this.time.delayedCall(450, () => this.wepTxt.setColor(COLORS.text)) // scene clock → auto-cleared on shutdown
    }
    // Dim a rest button when that resource is already full — resting then only
    // wastes a world step. Still tappable (gameplay unchanged); the cue is advisory.
    this.sleepBtn?.setAlpha(p.sp >= p.msp ? 0.5 : 1)
    this.healBtn?.setAlpha(p.hp >= p.mhp ? 0.5 : 1)

    // Only the latest important line stays on the main playfield; the full recent log lives in
    // a drawer so normal movement does not feel like reading a debug console.
    const feed = matchController.getMatch().log.filter((l) => l.tone === 'system' || l.tone === 'bad').slice(-1).map((l) => `${t('explore.log.open')} · ${l.text}`)
    this.feedTxt.setScale(1) // reset before measuring (a prior long line may have shrunk it)
    this.feedTxt.setText(feed.join('\n'))
    // Shrink a long localized line (e.g. a broadcast) so it never clips off the right edge.
    const maxFeedW = DESIGN_WIDTH - 44
    if (this.feedTxt.width > maxFeedW) this.feedTxt.setScale(maxFeedW / this.feedTxt.width)

    const inDanger = new Set(matchController.getNextForbidden()).has(this.zone)
    this.setDanger(inDanger)
  }

  /** Show + pulse the danger warning when the current zone is next to be sealed.
   *  The pulse tween is created once and torn down when the danger clears. */
  private setDanger(active: boolean): void {
    if (active) {
      this.dangerTxt.setText(t('hud.danger'))
      // The warning text (red) conveys the danger; the alpha pulse is just urgency —
      // skip it under prefers-reduced-motion (the text stays fully visible).
      if (!this.dangerTween && !prefersReducedMotion()) {
        this.dangerTween = this.tweens.add({
          targets: this.dangerTxt,
          alpha: { from: 1, to: 0.3 },
          duration: 520,
          yoyo: true,
          repeat: -1,
        })
      }
    } else {
      this.dangerTween?.stop()
      this.dangerTween = undefined
      this.dangerTxt.setAlpha(1).setText('')
    }
  }

  // ─── room building ───────────────────────────────────────────────────────

  private isWall(gx: number, gy: number): boolean {
    const border = gx === 0 || gy === 0 || gx === GW - 1 || gy === GH - 1
    return border && !this.exitTiles.has(key(gx, gy))
  }

  private tileXY(gx: number, gy: number): { x: number; y: number } {
    return { x: ROOM_X + gx * TILE + TILE / 2, y: ROOM_Y + gy * TILE + TILE / 2 }
  }

  private computeExits(zone: number): void {
    this.exitTiles = new Map()
    const midX = Math.floor(GW / 2)
    const midY = Math.floor(GH / 2)
    const tileFor: Record<Dir, [number, number]> = {
      N: [midX, 0],
      S: [midX, GH - 1],
      E: [GW - 1, midY],
      W: [0, midY],
    }
    for (const d of DIRS) {
      if (neighborOf(zone, d) !== undefined) {
        const [gx, gy] = tileFor[d]
        this.exitTiles.set(key(gx, gy), d)
      }
    }
  }

  private entryTile(opp: Dir): [number, number] {
    const midX = Math.floor(GW / 2)
    const midY = Math.floor(GH / 2)
    if (opp === 'N') return [midX, 1]
    if (opp === 'S') return [midX, GH - 2]
    if (opp === 'E') return [GW - 2, midY]
    return [1, midY]
  }

  private freeInteriorTile(): [number, number] | null {
    const taken = new Set<string>([key(this.px, this.py)])
    for (const b of this.botSprites.values()) taken.add(key(b.gx, b.gy))
    for (const k of this.lootAt.keys()) taken.add(k)
    const candidates: Array<[number, number]> = []
    for (let gx = 1; gx < GW - 1; gx++) {
      for (let gy = 1; gy < GH - 1; gy++) {
        if (this.isWall(gx, gy) || this.blocked.has(key(gx, gy))) continue
        if (taken.has(key(gx, gy))) continue
        candidates.push([gx, gy])
      }
    }
    if (candidates.length === 0) return null
    return candidates[Math.floor(Math.random() * candidates.length)]
  }

  private buildRoom(zone: number, fromDir: Dir | undefined): void {
    this.zone = zone
    this.computeExits(zone)
    this.blocked = new Set()

    const entry = fromDir ? this.entryTile(opposite(fromDir)) : [Math.floor(GW / 2), Math.floor(GH / 2)]
    this.px = entry[0]
    this.py = entry[1]

    const theme = zoneTheme(ZONES[zone].theme)
    const layer = this.add.container(0, 0)

    // Theme-aware floor from the purchased RPG Urban Pack subset. Purely visual:
    // collision/exit logic still uses the grid, so readability stays governed by tile states.
    layer.add(this.add.tileSprite(ROOM_X, ROOM_Y, GW * TILE, GH * TILE, zoneFloorTile(ZONES[zone].theme)).setOrigin(0, 0))

    // leafy/rocky border on every wall tile that isn't an exit
    for (let gx = 0; gx < GW; gx++) {
      for (let gy = 0; gy < GH; gy++) {
        if (!this.isWall(gx, gy)) continue
        const { x, y } = this.tileXY(gx, gy)
        const img = this.add.image(x, y, borderObj(theme.border))
        img.setScale((TILE + 10) / img.width).setOrigin(0.5, 0.82)
        layer.add(img)
      }
    }

    // exit arrows + destination labels
    const arrow: Record<Dir, string> = { N: '▲', S: '▼', E: '▶', W: '◀' }
    // Hollow counterparts for the "about to seal" tier — the exit tile's edge position
    // already conveys direction, so the glyph SHAPE is free to encode danger too.
    const hollowArrow: Record<Dir, string> = { N: '△', S: '▽', E: '▷', W: '◁' }
    const nextForbidden = matchController.getNextForbidden()
    for (const [k, d] of this.exitTiles) {
      const [gx, gy] = k.split(',').map(Number)
      const { x, y } = this.tileXY(gx, gy)
      const nz = neighborOf(zone, d)
      // Encode the neighbour's danger by BOTH colour and glyph shape, so routing risk
      // reads even for colour-blind players (hue alone isn't enough): filled arrow +
      // green = safe, hollow arrow + amber = about to seal (still passable), ✕ + red =
      // already sealed. Shape carries the meaning; colour reinforces it.
      const forbidden = nz !== undefined && matchController.isZoneForbidden(nz)
      const nextSeal = nz !== undefined && !forbidden && nextForbidden.includes(nz)
      const exitColor = forbidden ? COLORS.bad : nextSeal ? '#ffb24d' : COLORS.good
      const glyph = forbidden ? '✕' : nextSeal ? hollowArrow[d] : arrow[d]
      layer.add(this.add.text(x, y, glyph, { fontFamily: FONT, fontSize: '28px', color: exitColor }).setOrigin(0.5).setStroke('#0b0e14', 4))
      if (nz !== undefined) {
        const lx = d === 'E' ? x - 34 : d === 'W' ? x + 34 : x
        const ly = d === 'N' ? y + 22 : d === 'S' ? y - 22 : y
        // Show the WHOLE destination name (no misleading mid-word cut), scaled down to
        // the same compact width budget the old 4-char truncation occupied — so a long
        // localized name (en-US "Lighthouse", CJK "觀光協會大樓") stays readable in full
        // and the label never grows wider than before (no new overlap).
        const destTxt = this.add.text(lx, ly, zoneName(nz), { fontFamily: FONT, fontSize: '11px', color: exitColor }).setOrigin(0.5).setStroke('#0b0e14', 3)
        const maxLabelW = 48
        if (destTxt.width > maxLabelW) destTxt.setScale(maxLabelW / destTxt.width)
        layer.add(destTxt)
      }
    }

    // A themed centrepiece near the top, blocked so you walk around it. Keep it
    // off every exit's interior approach tile; the north exit in particular is
    // reached through the old default position at [midX, 1].
    const exitApproaches = new Set(
      [...this.exitTiles.values()].map((dir) => {
        const [gx, gy] = this.entryTile(dir)
        return key(gx, gy)
      }),
    )
    const centreCandidates: Array<[number, number]> = [
      [Math.floor(GW / 2), 1],
      [Math.floor(GW / 2) - 1, 2],
      [Math.floor(GW / 2) + 1, 2],
    ]
    const centre = centreCandidates.find(([gx, gy]) =>
      !(this.px === gx && this.py === gy) && !exitApproaches.has(key(gx, gy)),
    )
    if (centre) {
      const [cgx, cgy] = centre
      const { x, y } = this.tileXY(cgx, cgy)
      const c = this.add.image(x, y + 6, theme.center)
      c.setScale((TILE * 1.6) / c.width).setOrigin(0.5, 0.78)
      layer.add(c)
      this.blocked.add(key(cgx, cgy))
    }

    // Purchased Urban Pack props: at most two per built zone, kept in interior corners.
    // They are blocked so bots/loot/player never overlap the art, and none of these positions
    // can be an exit approach. Sparse placement protects combat and route readability.
    const urbanProps = zoneUrbanProps(ZONES[zone].theme)
    const urbanPropTiles: Array<[number, number]> = [[1, 1], [GW - 2, GH - 2]]
    urbanProps.slice(0, urbanPropTiles.length).forEach((texture, i) => {
      const [gx, gy] = urbanPropTiles[i]
      if ((this.px === gx && this.py === gy) || this.blocked.has(key(gx, gy))) return
      const { x, y } = this.tileXY(gx, gy)
      const prop = this.add.image(x, y, texture)
      prop.setScale((TILE * 0.86) / Math.max(prop.width, prop.height)).setOrigin(0.5)
      layer.add(prop)
      this.blocked.add(key(gx, gy))
    })

    this.lootAt = new Map()
    this.botSprites = new Map()

    // player
    const pxy = this.tileXY(this.px, this.py)
    this.playerSpr = this.add.sprite(pxy.x, pxy.y - 6, 'char_idle', 0).setScale(1.05)
    layer.add(this.playerSpr)
    // Subtle "breathing" idle (scale only — doesn't touch the x/y move/bump tweens).
    // Recreated per room; the prior tween dies with the old sprite/layer. Purely
    // decorative (conveys no state), so skip it under prefers-reduced-motion.
    this.breatheTween?.stop()
    this.breatheTween = undefined
    if (!prefersReducedMotion()) {
      this.breatheTween = this.tweens.add({ targets: this.playerSpr, scale: 1.09, duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.InOut' })
    }
    this.layer = layer

    // loot + bots
    const lootCount = Math.floor(Math.random() * 3)
    for (let i = 0; i < lootCount; i++) this.spawnLoot()
    for (const bot of matchController.getOccupantsAt(zone)) this.addBotSprite(bot)

    if (this.isFoggy()) {
      layer.add(this.add.rectangle(ROOM_X, ROOM_Y, GW * TILE, GH * TILE, 0x8aa0b8, 0.22).setOrigin(0, 0))
    }
    this.applyFog()
    this.showAreaCard()
  }

  private isFoggy(): boolean {
    return FOG_WEATHER.has(matchController.getWeather())
  }

  // Hide bots beyond the player's vision radius when it's foggy.
  private applyFog(): void {
    const foggy = this.isFoggy()
    for (const b of this.botSprites.values()) {
      const seen = !foggy || Math.abs(b.gx - this.px) + Math.abs(b.gy - this.py) <= FOG_VISION
      b.spr.setVisible(seen)
      b.tag.setVisible(seen)
    }
  }

  private tileTakenByOther(gx: number, gy: number, self: BotSpr): boolean {
    for (const b of this.botSprites.values()) if (b !== self && b.gx === gx && b.gy === gy) return true
    return false
  }

  // Bots roam their room tile-to-tile so the island feels alive (visual only;
  // their authoritative zone is unchanged — they still fight/flee via the sim).
  private wanderBots(): void {
    if (this.inCombat || this.transitioning) return
    for (const b of this.botSprites.values()) {
      if (Math.random() > 0.55) continue
      const cand: Array<[number, number]> = ([[0, -1], [0, 1], [-1, 0], [1, 0]] as Array<[number, number]>)
        .map(([dx, dy]) => [b.gx + dx, b.gy + dy] as [number, number])
        .filter(
          ([gx, gy]) =>
            gx > 0 &&
            gy > 0 &&
            gx < GW - 1 &&
            gy < GH - 1 &&
            !this.isWall(gx, gy) &&
            !this.blocked.has(key(gx, gy)) &&
            !(gx === this.px && gy === this.py) &&
            !this.lootAt.has(key(gx, gy)) &&
            !this.tileTakenByOther(gx, gy, b),
        )
      if (cand.length === 0) continue
      const [ngx, ngy] = cand[Math.floor(Math.random() * cand.length)]
      b.gx = ngx
      b.gy = ngy
      const { x, y } = this.tileXY(ngx, ngy)
      // Under prefers-reduced-motion the bot snaps to its new tile (no slide) — moving
      // sprites are vestibular motion; the new position is the only information that matters
      // (parity with the gated room-slide / breathing). Otherwise it glides over 260ms.
      if (prefersReducedMotion()) {
        b.spr.setPosition(x, y - 6)
        b.tag.setPosition(x, y - 32)
      } else {
        this.tweens.add({ targets: b.spr, x, y: y - 6, duration: 260 })
        this.tweens.add({ targets: b.tag, x, y: y - 32, duration: 260 })
      }
    }
    this.applyFog()
  }

  // A compact fading location chip on entering a room. Long description/lore live in Area Intel.
  private showAreaCard(): void {
    this.areaCard?.destroy()
    const occupants = matchController.getOccupantsAt(this.zone)
    const foes = occupants.length
    const c = this.add.container(DESIGN_WIDTH / 2, 124).setDepth(15)
    const panel = this.add.rectangle(0, 0, 244, 46, 0x0b1018, 0.78).setOrigin(0.5).setStrokeStyle(1, COLORS.panelEdge)
    panel.setInteractive({ useHandCursor: true }).on('pointerup', () => this.showAreaDrawer())
    const name = this.add.text(0, -8, `${zoneName(this.zone)}　${ZONES[this.zone].coord}`, {
      fontFamily: FONT,
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setStroke('#0b0e14', 4)
    const maxNameW = 218
    if (name.width > maxNameW) name.setScale(maxNameW / name.width)
    const chips: string[] = [t('explore.intel.open')]
    // Enemy-presence cue: how many rivals share this zone — useful when fog hides
    // their positions (you still sense the threat level). Hidden in a clear zone.
    if (foes > 0) {
      // Flag the escape key on the summary card too if its holder is in this zone (🔑) —
      // the fading card mirrors the persistent bot-sprite marker (#128) at a glance.
      chips.push(t('explore.areaEnemies', { n: foes }) + (occupants.some((o) => o.hasKey) ? ' 🔑' : '') + (occupants.some((o) => o.isGhost) ? ' 👻' : ''))
    }
    const meta = this.add.text(0, 12, chips.join(' · '), {
      fontFamily: FONT,
      fontSize: '10px',
      color: foes > 0 ? COLORS.bad : COLORS.sub,
      fontStyle: foes > 0 ? 'bold' : 'normal',
    }).setOrigin(0.5).setStroke('#0b0e14', 3)
    if (meta.width > 218) meta.setScale(218 / meta.width)
    c.add([panel, name, meta])
    const firstVisit = !this.visitedZones.has(this.zone)
    this.visitedZones.add(this.zone)
    this.areaCard = c
    const hold = firstVisit ? 2200 : 1600
    const done = (): void => {
      c.destroy()
      if (this.areaCard === c) this.areaCard = undefined
    }
    // Under prefers-reduced-motion the card appears instantly and is removed after the read
    // delay with no alpha animation (parity with the gated Gacha/Story reveals); otherwise it
    // eases in and fades out. The scene clock auto-clears the delayed call on shutdown.
    if (prefersReducedMotion()) {
      c.setAlpha(1)
      this.time.delayedCall(hold + 700, done)
    } else {
      c.setAlpha(0)
      this.tweens.add({ targets: c, alpha: 1, duration: 180 })
      this.tweens.add({ targets: c, alpha: 0, delay: hold, duration: 700, onComplete: done })
    }
  }

  private showObjectiveCard(): void {
    this.objectiveCard?.destroy()
    const c = this.add.container(28, 628).setDepth(24)
    const panel = this.add.rectangle(0, 0, DESIGN_WIDTH - 56, 66, 0x0b1018, 0.86).setOrigin(0, 0).setStrokeStyle(1, COLORS.zoneHere)
    const title = this.add.text(14, 10, t('explore.objective.title'), {
      fontFamily: FONT, fontSize: '13px', color: COLORS.good, fontStyle: 'bold',
    })
    const bodyKey = matchController.isRanked() ? 'explore.objective.ranked' : 'explore.objective.casual'
    const body = this.add.text(14, 31, t(bodyKey), {
      fontFamily: FONT, fontSize: '12px', color: COLORS.text,
      wordWrap: { width: DESIGN_WIDTH - 88, useAdvancedWrap: true },
    })
    c.add([panel, title, body])
    this.objectiveCard = c
    const done = (): void => {
      c.destroy()
      if (this.objectiveCard === c) this.objectiveCard = undefined
    }
    if (prefersReducedMotion()) {
      this.time.delayedCall(5200, done)
    } else {
      c.setAlpha(0)
      this.tweens.add({ targets: c, alpha: 1, duration: 180 })
      this.tweens.add({ targets: c, alpha: 0, delay: 4600, duration: 650, onComplete: done })
    }
  }

  private spawnLoot(): void {
    const tile = this.freeInteriorTile()
    if (!tile) return
    const weapon = Math.random() < 0.5
    const loot: Loot = weapon
      ? { kind: 'weapon', weapon: LOOT_WEAPONS[Math.floor(Math.random() * LOOT_WEAPONS.length)] }
      : { kind: 'item', item: LOOT_ITEMS[Math.floor(Math.random() * LOOT_ITEMS.length)] }
    const { x, y } = this.tileXY(tile[0], tile[1])
    const spr = this.add.image(x, y - 4, objKey('crate'))
    spr.setScale(36 / spr.width).setOrigin(0.5, 0.85)
    // Tint by category so weapon vs supply reads at a glance (the exact item stays a
    // surprise until pickup): warm = weapon, green = supply.
    spr.setTint(weapon ? 0xffb080 : 0x9fe0b0)
    this.layer.add(spr)
    this.lootAt.set(key(tile[0], tile[1]), { loot, spr })
  }

  private addBotSprite(bot: OccupantView): void {
    const tile = this.freeInteriorTile()
    if (!tile) return
    const { x, y } = this.tileXY(tile[0], tile[1])
    const spr = this.add.sprite(x, y - 6, 'char_idle', 0).setScale(1.0)
    // Ghosts (real rivals' recorded runs) render translucent + purple so they read as "another
    // player", distinct from the AI bots.
    if (bot.isGhost) spr.setTint(0xc9a3ff).setAlpha(0.68)
    else spr.setTint(ARCH_TINT[bot.archetype as keyof typeof ARCH_TINT] ?? 0xffffff)
    // Seat number from the STABLE entity id (B{index} → index+1) rather than parsing the
    // display name — robust if the name format ever changes (e.g. a name-pool realignment
    // that drops the numeric suffix would otherwise leave the tag blank). Falls back to the
    // name's digits if the id isn't the expected B{n} shape.
    const tag = this.add.text(x, y - 32, '', { fontFamily: FONT, fontSize: '11px', fontStyle: 'bold' }).setOrigin(0.5).setStroke('#0b0e14', 3)
    this.styleBotTag(tag, bot)
    this.layer.add(spr)
    this.layer.add(tag)
    this.botSprites.set(bot.id, { spr, tag, gx: tile[0], gy: tile[1] })
  }

  /** Seat number from the STABLE entity id (B{index} → index+1), falling back to the name's
   *  digits if the id isn't the expected B{n} shape (robust to a name-format change). */
  private botSeatNum(bot: OccupantView): string {
    const idNum = Number.parseInt(bot.id.replace(/\D/g, ''), 10)
    return Number.isNaN(idNum) ? bot.name.replace(/\D/g, '') : String(idNum + 1)
  }

  /** Flag a key-holding bot with 🔑 in gold (pre-combat intel: kill this one to claim the escape
   *  key) — the Explore mirror of the Combat key marker (#127). Re-applied on every reconcile so
   *  the marker FOLLOWS the key if it transfers to an already-shown bot (keyholder slain by a
   *  co-located rival), instead of going stale at the dead holder. */
  private styleBotTag(tag: Phaser.GameObjects.Text, bot: OccupantView): void {
    // Ghosts show 👻 + the rival's handle (they're real players, not numbered classmates).
    if (bot.isGhost) {
      const k = bot.hasKey ? '🔑' : ''
      tag.setText(`👻${k}${bot.name}`).setColor('#c9a3ff')
      return
    }
    const num = this.botSeatNum(bot)
    tag.setText(bot.hasKey ? `🔑${num}` : num).setColor(bot.hasKey ? '#ffd84d' : '#ffe0e0')
  }

  // ─── controls ───────────────────────────────────────────────────────────

  private buildControls(): void {
    // Bigger D-pad (66px vs 60): the most-tapped control needs to clear the ~44pt touch-target
    // floor even when FIT scales the 540-wide canvas down on a small phone (~0.7× → ~46px),
    // and the cross is spread to a 4px inter-button gap (was 2px) to cut mis-taps between
    // adjacent arrows. Footprint stays clear of the room above (ends y712), the canvas bottom
    // (S ends y953), and the rest/escape buttons (far right) — see buildControls layout below.
    const pad = (x: number, y: number, label: string, dir: Dir): void => {
      makeButton(this, x, y, { width: 66, height: 66, label, fontSize: 28, onTap: () => this.tryStep(dir) })
    }
    pad(120, 780, '▲', 'N')
    pad(120, 920, '▼', 'S')
    pad(50, 850, '◀', 'W')
    pad(190, 850, '▶', 'E')

    this.sleepBtn = makeButton(this, 330, 800, { width: 120, height: 52, label: t('rest.sleep'), fontSize: 17, onTap: () => gameBus.emit('intent:rest', { kind: 'sleep' }) })
    this.healBtn = makeButton(this, 462, 800, { width: 120, height: 52, label: t('rest.heal'), fontSize: 17, onTap: () => gameBus.emit('intent:rest', { kind: 'heal' }) })
    this.escBtn = makeButton(this, 396, 862, { width: 252, height: 50, label: t('action.escape') + ' 🔑', fontSize: 18, fill: COLORS.zoneSafe, onTap: () => this.tryEscape() })
    // Quit-to-menu — without this a player is trapped in a match until it ends (a dead end;
    // CLAUDE.md "no dead ends"). Bottom-right, in the free strip below the escape button and
    // clear of the left D-pad. A deliberate confirm guards an accidental tap (abandons the run).
    makeButton(this, 470, 920, { width: 130, height: 44, label: t('explore.quit'), fontSize: 15, onTap: () => this.showQuitConfirm() })
    // Desktop-only control hint (parity with Combat/Story/Settings) — Explore has the most
    // shortcuts (move/rest/escape) yet had none. Bottom-right, clear of the bottom-left
    // D-pad and the escape button above; hidden on touch (no keys there). Fit-to-width.
    if (hasFinePointer()) {
      const kbd = this.add.text(DESIGN_WIDTH - 12, DESIGN_HEIGHT - 8, t('explore.kbd'), { fontFamily: FONT, fontSize: '11px', color: COLORS.sub }).setOrigin(1, 1)
      const maxKbdW = DESIGN_WIDTH - 24
      if (kbd.width > maxKbdW) kbd.setScale(maxKbdW / kbd.width)
    }
  }

  private tryEscape(): void {
    if (this.hasModalOpen()) return // modal/drawer is open — keyboard 'E' must not leak through
    if (!this.player().hasKey) {
      this.toast(t('fail.noKey'))
      return
    }
    gameBus.emit('intent:escape', {})
  }

  /** Confirm abandoning the match before returning to the menu (shared modal panel) — an
   *  accidental quit would throw away an in-progress run. Reuses the shared menu/cancel labels.
   *  Tracked in `quitBox`, torn down on accept/cancel/shutdown. */
  private showQuitConfirm(): void {
    if (this.hasModalOpen() || this.inCombat) return // not over a combat hand-off; not stacked
    this.quitBox = makeConfirmPanel(this, {
      message: t('explore.quitConfirm'),
      confirmLabel: t('story.toMenu'),
      cancelLabel: t('gacha.confirm.no'),
      confirmFill: COLORS.btnDanger,
      onConfirm: () => { this.dismissQuitConfirm(); this.scene.start('Menu') },
      onCancel: () => this.dismissQuitConfirm(),
    }).setDepth(300)
  }

  private dismissQuitConfirm(): void {
    this.quitBox?.destroy() // destroys children + their listeners
    this.quitBox = undefined
  }

  private showLogDrawer(): void {
    if (this.hasModalOpen() || this.inCombat) return
    const backdrop = this.add.rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT, 0x05080d, 0.28).setOrigin(0, 0).setInteractive()
    backdrop.on('pointerup', () => this.dismissLogDrawer())
    const panelH = 332
    const panelY = DESIGN_HEIGHT - panelH
    const panel = this.add.rectangle(DESIGN_WIDTH / 2, panelY, DESIGN_WIDTH - 40, panelH, 0x101722, 1)
      .setOrigin(0.5, 0)
      .setStrokeStyle(2, COLORS.panelEdge)
    const title = this.add.text(36, panelY + 22, t('explore.log.title'), {
      fontFamily: FONT,
      fontSize: '18px',
      color: COLORS.text,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5)
    const lines = matchController.getMatch().log.slice(-8)
    const body = this.add.text(36, panelY + 52, lines.map((l) => `• ${l.text}`).join('\n'), {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.normal,
      lineSpacing: 7,
      wordWrap: { width: DESIGN_WIDTH - 72, useAdvancedWrap: true },
    }).setOrigin(0, 0)
    if (body.height > panelH - 112) body.setScale((panelH - 112) / body.height)
    const close = makeButton(this, DESIGN_WIDTH / 2, DESIGN_HEIGHT - 38, {
      width: 220,
      height: 48,
      label: t('explore.log.close'),
      fontSize: 17,
      onTap: () => this.dismissLogDrawer(),
    })
    this.logDrawer = this.add.container(0, 0, [backdrop, panel, title, body, close]).setDepth(260)
    if (!prefersReducedMotion()) {
      panel.setY(panelY + 24)
      title.setY(title.y + 24)
      body.setY(body.y + 24)
      close.setY(close.y + 24)
      this.tweens.add({ targets: [panel, title, body, close], y: '-=24', duration: 180, ease: 'Cubic.Out' })
    }
  }

  private dismissLogDrawer(): void {
    this.logDrawer?.destroy()
    this.logDrawer = undefined
  }

  private showAreaDrawer(): void {
    if (this.areaDrawer || this.logDrawer || this.quitBox || this.inCombat) return
    const occupants = matchController.getOccupantsAt(this.zone)
    const hasKey = occupants.some((o) => o.hasKey)
    const hasGhost = occupants.some((o) => o.isGhost)
    const backdrop = this.add.rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT, 0x05080d, 0.28).setOrigin(0, 0).setInteractive()
    backdrop.on('pointerup', () => this.dismissAreaDrawer())
    const panelH = 360
    const panelY = DESIGN_HEIGHT - panelH
    const panel = this.add.rectangle(DESIGN_WIDTH / 2, panelY, DESIGN_WIDTH - 40, panelH, 0x101722, 1)
      .setOrigin(0.5, 0)
      .setStrokeStyle(2, COLORS.panelEdge)
    const title = this.add.text(36, panelY + 22, `${zoneName(this.zone)}　${ZONES[this.zone].coord}`, {
      fontFamily: FONT,
      fontSize: '18px',
      color: COLORS.text,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5)
    if (title.width > DESIGN_WIDTH - 180) title.setScale((DESIGN_WIDTH - 180) / title.width)
    const statusBits = [
      t('explore.intel.enemies', { n: occupants.length }),
      hasKey ? t('explore.intel.key') : '',
      hasGhost ? t('explore.intel.ghost') : '',
    ].filter(Boolean)
    const status = this.add.text(DESIGN_WIDTH - 36, panelY + 22, statusBits.join(' · '), {
      fontFamily: FONT,
      fontSize: '11px',
      color: occupants.length > 0 ? COLORS.bad : COLORS.good,
      align: 'right',
    }).setOrigin(1, 0.5)
    if (status.width > 164) status.setScale(164 / status.width)
    const desc = this.add.text(36, panelY + 56, zoneDesc(this.zone), {
      fontFamily: FONT,
      fontSize: '13px',
      color: COLORS.normal,
      lineSpacing: 5,
      wordWrap: { width: DESIGN_WIDTH - 72, useAdvancedWrap: true },
    }).setOrigin(0, 0)
    const loreTitle = this.add.text(36, panelY + 126, t('explore.intel.lore'), {
      fontFamily: FONT,
      fontSize: '12px',
      color: '#c9b88a',
      fontStyle: 'bold',
    }).setOrigin(0, 0)
    const lore = this.add.text(36, panelY + 150, zoneLore(this.zone), {
      fontFamily: FONT,
      fontSize: '12px',
      color: '#c9b88a',
      fontStyle: 'italic',
      lineSpacing: 5,
      wordWrap: { width: DESIGN_WIDTH - 72, useAdvancedWrap: true },
    }).setOrigin(0, 0)
    const close = makeButton(this, DESIGN_WIDTH / 2, DESIGN_HEIGHT - 38, {
      width: 220,
      height: 48,
      label: t('explore.intel.close'),
      fontSize: 17,
      onTap: () => this.dismissAreaDrawer(),
    })
    const contentMaxH = DESIGN_HEIGHT - 86 - lore.y
    if (lore.height > contentMaxH) lore.setScale(contentMaxH / lore.height)
    this.areaDrawer = this.add.container(0, 0, [backdrop, panel, title, status, desc, loreTitle, lore, close]).setDepth(255)
    if (!prefersReducedMotion()) {
      for (const o of [panel, title, status, desc, loreTitle, lore, close]) o.setY(o.y + 24)
      this.tweens.add({ targets: [panel, title, status, desc, loreTitle, lore, close], y: '-=24', duration: 180, ease: 'Cubic.Out' })
    }
  }

  private dismissAreaDrawer(): void {
    this.areaDrawer?.destroy()
    this.areaDrawer = undefined
  }

  // ─── stepping / transitions ───────────────────────────────────────────────

  private tryStep(dir: Dir): void {
    if (this.inCombat || this.transitioning || this.hasModalOpen()) return // modal/drawer blocks pointer, not keys
    const d = { N: [0, -1], S: [0, 1], E: [1, 0], W: [-1, 0] }[dir]
    const tx = this.px + d[0]
    const ty = this.py + d[1]
    if (tx < 0 || ty < 0 || tx >= GW || ty >= GH) return

    const exitDir = this.exitTiles.get(key(tx, ty))
    if (exitDir) {
      this.transition(exitDir)
      return
    }
    if (this.isWall(tx, ty) || this.blocked.has(key(tx, ty))) {
      // "Can't go there" feedback. Under prefers-reduced-motion the positional lurch is
      // replaced by a brief tint flash — the bump is the ONLY blocked-direction cue, so
      // it must NOT just vanish (that would be a silent failure). Otherwise nudge toward
      // the wall: base it on the true tile centre (not the live sprite x/y) and guard
      // against stacking, so rapid taps never drift the sprite off its tile.
      if (prefersReducedMotion()) {
        this.playerSpr.setTint(0xff8a8a)
        this.time.delayedCall(160, () => this.playerSpr.clearTint()) // scene clock → auto-cleared on shutdown
      } else if (!this.bumpTween?.isPlaying()) {
        const home = this.tileXY(this.px, this.py)
        this.bumpTween = this.tweens.add({
          targets: this.playerSpr,
          x: home.x + d[0] * 6,
          y: home.y - 6 + d[1] * 6,
          duration: 70,
          yoyo: true,
          onComplete: () => this.playerSpr.setPosition(home.x, home.y - 6),
        })
      }
      return
    }
    // bot on target tile → fight it
    for (const [id, b] of this.botSprites) {
      if (b.gx === tx && b.gy === ty) {
        this.startCombat(id)
        return
      }
    }
    // loot on target tile → grab, then move on
    const loot = this.lootAt.get(key(tx, ty))
    if (loot) {
      let gained = t('explore.gotLoot') // generic fallback (already prefixed)
      if (loot.loot.kind === 'weapon' && loot.loot.weapon) {
        gameBus.emit('intent:pickupWeapon', { weapon: loot.loot.weapon })
        gained = `＋${weaponName(loot.loot.weapon.name)}`
      } else if (loot.loot.item) {
        gameBus.emit('intent:pickupItem', { item: loot.loot.item })
        gained = `＋${itemName(loot.loot.item)}`
      }
      const lxy = this.tileXY(tx, ty)
      this.floatPickup(lxy.x, lxy.y, gained)
      loot.spr.destroy()
      this.lootAt.delete(key(tx, ty))
    }
    this.px = tx
    this.py = ty
    const { x, y } = this.tileXY(tx, ty)
    if (d[0] !== 0) this.playerSpr.setFlipX(d[0] < 0)
    // Under prefers-reduced-motion the player snaps to the tile (no walk-glide) — the new
    // position is the information; the slide is flourish (parity with the gated bot move
    // #177). Intra-room position is Explore-only/cosmetic (the sim is zone-based), so this
    // doesn't touch flag-off gameplay.
    if (prefersReducedMotion()) {
      this.playerSpr.setPosition(x, y - 6).setFrame(0)
    } else {
      this.playerSpr.play('walk', true)
      this.tweens.add({
        targets: this.playerSpr,
        x,
        y: y - 6,
        duration: 110,
        onComplete: () => {
          this.playerSpr.stop()
          this.playerSpr.setFrame(0)
        },
      })
    }
  }

  private transition(dir: Dir): void {
    const nz = neighborOf(this.zone, dir)
    if (nz === undefined) return
    if (matchController.isZoneForbidden(nz)) {
      this.toast(t('fail.forbidden'))
      return
    }
    const p = this.player()
    const cost = p.injuries.has('f') ? MOVE_COST_HURT : MOVE_COST
    if (p.sp <= cost) {
      this.toast(t('fail.noSp'))
      return
    }

    this.transitioning = true
    const oldLayer = this.layer
    // advance the authoritative state (zone change + world tick)
    gameBus.emit('intent:move', { zone: nz })

    // build the destination room, then slide it in from `dir`
    this.buildRoom(nz, dir)
    // Reduced-motion: skip the full-screen slide (disorienting) — cut straight to the new
    // room. The room itself is the information; the slide is only flourish.
    if (prefersReducedMotion()) {
      this.layer.setPosition(0, 0)
      oldLayer.destroy()
      this.transitioning = false
      this.renderHud()
      return
    }
    const off = (dir === 'E' || dir === 'W' ? GW * TILE : GH * TILE) + 24
    const vx = dir === 'E' ? off : dir === 'W' ? -off : 0
    const vy = dir === 'S' ? off : dir === 'N' ? -off : 0
    this.layer.setPosition(vx, vy)
    this.tweens.add({ targets: this.layer, x: 0, y: 0, duration: 240, ease: 'Cubic.Out' })
    this.tweens.add({
      targets: oldLayer,
      x: -vx,
      y: -vy,
      duration: 240,
      ease: 'Cubic.Out',
      onComplete: () => {
        oldLayer.destroy()
        this.transitioning = false
        this.renderHud()
      },
    })
  }

  // ─── combat hand-off ───────────────────────────────────────────────────────

  private startCombat(botId: string): void {
    if (this.inCombat) return
    this.inCombat = true
    gameBus.emit('intent:beginCombat', { targetId: botId })
    this.scene.launch('Combat', { targetId: botId })
    this.scene.pause()
  }

  private afterCombat(): void {
    this.inCombat = false
    // drop sprites of bots that died / left, add any that wandered in
    this.reconcileBots()
    this.renderHud()
  }

  private reconcileBots(): void {
    const here = matchController.getOccupantsAt(this.zone)
    const present = new Set(here.map((b) => b.id))
    for (const [id, b] of [...this.botSprites]) {
      if (!present.has(id)) {
        b.spr.destroy()
        b.tag.destroy()
        this.botSprites.delete(id)
      }
    }
    for (const bot of here) {
      const existing = this.botSprites.get(bot.id)
      if (!existing) this.addBotSprite(bot)
      else this.styleBotTag(existing.tag, bot) // refresh 🔑 if the key transferred to a shown bot
    }
    this.applyFog()
  }

  // ─── signals ────────────────────────────────────────────────────────────────

  private onUpdated(): void {
    if (this.transitioning) {
      this.renderHud()
      return
    }
    if (!this.inCombat) this.reconcileBots()
    this.renderHud()
  }

  private onOver(): void {
    // match:over is re-emitted on every action once the match is over (e.g. a second
    // escape tap is rejected but still signals), so guard against firing the Result
    // transition more than once — otherwise we'd schedule multiple delayed scene.starts.
    if (this.over) return
    this.over = true
    this.idleTimer?.remove()
    // window timer works even while this scene is paused under the combat overlay (Phaser
    // scene timers pause there). Tracked so a shutdown before it fires can clear it.
    this.overTimer = window.setTimeout(() => {
      const { rank, total } = matchController.getRankView()
      const data: JKGame_ResultData = {
        reason: matchController.getWinReason() ?? 'dead',
        rank,
        total,
        kills: matchController.getPlayerView().kills,
        survived: matchController.getStep(),
      }
      if (this.scene.isActive('Combat') || this.scene.isPaused('Combat')) this.scene.stop('Combat')
      this.scene.start('Result', data)
    }, 850)
  }

  // Brief floating "+loot" cue at a tile when the player grabs supplies (the
  // authoritative log already names the item; this is just tactile feedback).
  private floatPickup(x: number, y: number, label: string): void {
    // Show what was actually grabbed (localized weapon/item name), not a generic tag.
    const tag = this.add.text(x, y - 10, label, {
      fontFamily: FONT, fontSize: '14px', color: COLORS.good, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(18).setStroke('#0b0e14', 3)
    const maxW = 180 // keep a long localized name from spilling across the room
    if (tag.width > maxW) tag.setScale(maxW / tag.width)
    // The label (what you grabbed) is the info; the upward drift is flourish — under
    // prefers-reduced-motion hold it in place and fade (parity with the Combat float #172).
    if (prefersReducedMotion()) {
      this.tweens.add({ targets: tag, alpha: 0, duration: 620, delay: 300, onComplete: () => tag.destroy() })
    } else {
      this.tweens.add({ targets: tag, y: y - 44, alpha: 0, duration: 620, onComplete: () => tag.destroy() })
    }
  }

  private toast(text: string): void {
    // Only one toast lives at a time: a new reason replaces any in-flight one instead of
    // stacking at the same spot (two semi-transparent boxes overlap into an unreadable blur,
    // which would defeat the whole point of explaining the failed action).
    this.activeToast?.destroy()
    const msg = this.add.text(DESIGN_WIDTH / 2, ROOM_Y + 40, text, {
      fontFamily: FONT,
      fontSize: '15px',
      color: COLORS.bad,
      backgroundColor: '#000000aa',
      padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(20)
    this.activeToast = msg
    const done = (): void => {
      msg.destroy()
      if (this.activeToast === msg) this.activeToast = undefined
    }
    // The message is the info; under prefers-reduced-motion fade it in place (no rise),
    // keeping the read-time delay so a failed-action reason is never missed.
    if (prefersReducedMotion()) {
      this.tweens.add({ targets: msg, alpha: 0, delay: 900, duration: 500, onComplete: done })
    } else {
      this.tweens.add({ targets: msg, alpha: 0, y: msg.y - 20, delay: 900, duration: 500, onComplete: done })
    }
  }

  private cleanup(): void {
    for (const dispose of this.disposers) dispose()
    this.disposers = []
    this.events.off(Phaser.Scenes.Events.RESUME, this.afterCombat, this) // scene-events leak on restart if not removed
    this.idleTimer?.remove()
    this.wanderTimer?.remove()
    this.dangerTween?.stop()
    this.dangerTween = undefined
    this.bumpTween?.stop()
    this.bumpTween = undefined
    this.hpBarTween?.stop()
    this.hpBarTween = undefined
    this.spBarTween?.stop()
    this.spBarTween = undefined
    this.breatheTween?.stop()
    this.breatheTween = undefined
    this.escapeTween?.stop()
    this.escapeTween = undefined
    this.activeToast?.destroy()
    this.activeToast = undefined
    if (this.overTimer !== undefined) window.clearTimeout(this.overTimer) // window timer isn't scene-managed
    this.overTimer = undefined
    this.areaCard?.destroy()
    this.objectiveCard?.destroy()
    this.dismissAreaDrawer()
    this.dismissLogDrawer()
    this.dismissQuitConfirm()
  }
}
